import { Schema } from "effect";
import { DesktopWorkspaceGrantIdSchema } from "./desktop-workspace";
import { compareUtf8Text, utf8ByteLength } from "./utf8";

export const DesktopGitBoundaryParseOptions = {
  errors: "all",
  onExcessProperty: "error",
  reportInput: false,
} as const;

export const DESKTOP_GIT_MAX_RELATIVE_PATH_BYTES = 4_096;
export const DESKTOP_GIT_MAX_CHANGED_FILES = 500;
export const DESKTOP_GIT_MAX_DIFF_BYTES = 512 * 1_024;
export const DESKTOP_GIT_DIFF_PROTOCOL_VERSION = 2;

const hasAsciiControlCharacter = (value: string): boolean => {
  for (const character of value) {
    const codePoint = character.codePointAt(0) ?? 0;
    if (codePoint <= 0x1f || codePoint === 0x7f) return true;
  }
  return false;
};

const isCanonicalRelativePath = (value: string): boolean =>
  value !== "" &&
  !value.startsWith("/") &&
  !value.includes("\\") &&
  !hasAsciiControlCharacter(value) &&
  utf8ByteLength(value) <= DESKTOP_GIT_MAX_RELATIVE_PATH_BYTES &&
  value.split("/").every((segment) => segment !== "" && segment !== "." && segment !== "..");

export const DesktopGitRelativePathSchema = Schema.String.check(
  Schema.makeFilter(
    (value) => isCanonicalRelativePath(value) || "must be a canonical relative Git path",
  ),
);

export const DesktopGitFileStateSchema = Schema.Literals([
  "unmodified",
  "modified",
  "type-changed",
  "added",
  "deleted",
  "renamed",
  "copied",
  "unmerged",
  "untracked",
]);

export const DesktopGitChangedFileSchema = Schema.Struct({
  path: DesktopGitRelativePathSchema,
  previousPath: Schema.NullOr(DesktopGitRelativePathSchema),
  indexState: DesktopGitFileStateSchema,
  worktreeState: DesktopGitFileStateSchema,
  conflicted: Schema.Boolean,
}).check(
  Schema.makeFilter((file) => {
    const renamedOrCopied = [file.indexState, file.worktreeState].some(
      (state) => state === "renamed" || state === "copied",
    );
    if ((file.previousPath !== null) !== renamedOrCopied) {
      return "previousPath must identify the source of a renamed or copied file";
    }
    const untracked = file.indexState === "untracked" || file.worktreeState === "untracked";
    return (
      !untracked ||
      (file.indexState === "untracked" && file.worktreeState === "untracked") ||
      "an untracked file must be untracked in both status columns"
    );
  }),
);

export const DesktopGitStatusInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
});

export const DesktopGitDiffScopeSchema = Schema.Literals(["all", "unstaged", "staged"]);

export const DesktopGitStatusResultSchema = Schema.Struct({
  version: Schema.Literal(1),
  files: Schema.Array(DesktopGitChangedFileSchema).check(
    Schema.isMaxLength(DESKTOP_GIT_MAX_CHANGED_FILES),
  ),
  truncated: Schema.Boolean,
}).check(
  Schema.makeFilter((result) => {
    let previousPath: string | undefined;
    for (const file of result.files) {
      if (previousPath !== undefined && compareUtf8Text(file.path, previousPath) <= 0) {
        return "changed files must have unique paths in ascending order";
      }
      previousPath = file.path;
    }
    return true;
  }),
);

export const DesktopGitDiffInputSchema = Schema.Struct({
  grantId: DesktopWorkspaceGrantIdSchema,
  relativePath: DesktopGitRelativePathSchema,
  scope: DesktopGitDiffScopeSchema,
});

export const DesktopGitDiffResultSchema = Schema.Struct({
  version: Schema.Literal(DESKTOP_GIT_DIFF_PROTOCOL_VERSION),
  relativePath: DesktopGitRelativePathSchema,
  scope: DesktopGitDiffScopeSchema,
  patch: Schema.String.check(
    Schema.makeFilter(
      (value) =>
        utf8ByteLength(value) <= DESKTOP_GIT_MAX_DIFF_BYTES ||
        `must contain at most ${DESKTOP_GIT_MAX_DIFF_BYTES} UTF-8 bytes`,
    ),
  ),
  truncated: Schema.Boolean,
});

export const DesktopGitErrorCodeSchema = Schema.Literals([
  "git-failed",
  "invalid-input",
  "invalid-response",
  "invalid-root",
  "not-repository",
  "output-too-large",
  "path-escape",
  "path-not-changed",
  "root-not-granted",
  "unavailable",
  "unsupported-content",
  "unsupported-path",
]);

export const DesktopGitErrorPayloadSchema = Schema.Struct({
  code: DesktopGitErrorCodeSchema,
  message: Schema.String.check(Schema.isMinLength(1), Schema.isMaxLength(240)),
  retryable: Schema.Boolean,
});

export type DesktopGitFileState = typeof DesktopGitFileStateSchema.Type;
export type DesktopGitChangedFile = typeof DesktopGitChangedFileSchema.Type;
export type DesktopGitStatusInput = typeof DesktopGitStatusInputSchema.Type;
export type DesktopGitStatusInputEncoded = typeof DesktopGitStatusInputSchema.Encoded;
export type DesktopGitStatusResult = typeof DesktopGitStatusResultSchema.Type;
export type DesktopGitStatusResultEncoded = typeof DesktopGitStatusResultSchema.Encoded;
export type DesktopGitDiffScope = typeof DesktopGitDiffScopeSchema.Type;
export type DesktopGitDiffInput = typeof DesktopGitDiffInputSchema.Type;
export type DesktopGitDiffInputEncoded = typeof DesktopGitDiffInputSchema.Encoded;
export type DesktopGitDiffResult = typeof DesktopGitDiffResultSchema.Type;
export type DesktopGitDiffResultEncoded = typeof DesktopGitDiffResultSchema.Encoded;
export type DesktopGitErrorCode = typeof DesktopGitErrorCodeSchema.Type;
export type DesktopGitErrorPayload = typeof DesktopGitErrorPayloadSchema.Type;
export type DesktopGitErrorPayloadEncoded = typeof DesktopGitErrorPayloadSchema.Encoded;

export const decodeDesktopGitStatusInput = Schema.decodeUnknownSync(
  DesktopGitStatusInputSchema,
  DesktopGitBoundaryParseOptions,
);
export const encodeDesktopGitStatusInput = Schema.encodeSync(
  DesktopGitStatusInputSchema,
  DesktopGitBoundaryParseOptions,
);
export const decodeDesktopGitStatusResult = Schema.decodeUnknownSync(
  DesktopGitStatusResultSchema,
  DesktopGitBoundaryParseOptions,
);
export const encodeDesktopGitStatusResult = Schema.encodeSync(
  DesktopGitStatusResultSchema,
  DesktopGitBoundaryParseOptions,
);
export const decodeDesktopGitDiffInput = Schema.decodeUnknownSync(
  DesktopGitDiffInputSchema,
  DesktopGitBoundaryParseOptions,
);
export const encodeDesktopGitDiffInput = Schema.encodeSync(
  DesktopGitDiffInputSchema,
  DesktopGitBoundaryParseOptions,
);
export const decodeDesktopGitDiffResult = Schema.decodeUnknownSync(
  DesktopGitDiffResultSchema,
  DesktopGitBoundaryParseOptions,
);
export const encodeDesktopGitDiffResult = Schema.encodeSync(
  DesktopGitDiffResultSchema,
  DesktopGitBoundaryParseOptions,
);
export const decodeDesktopGitErrorPayload = Schema.decodeUnknownSync(
  DesktopGitErrorPayloadSchema,
  DesktopGitBoundaryParseOptions,
);
export const encodeDesktopGitErrorPayload = Schema.encodeSync(
  DesktopGitErrorPayloadSchema,
  DesktopGitBoundaryParseOptions,
);
